// The ring of an incoming call (COMX-FE-01): a soft two-tone ring made with the Web Audio API (no
// audio file), repeated until it is stopped. Browsers refuse to start sound before the person has
// interacted with the page: when the audio context cannot run, the ring stays silent, no error.

const RING_SETTING_KEY = 'batiflow.callRingtone';

/** Whether the ring is on for this device (on by default); the setting never leaves the device. */
export function isRingEnabled(): boolean {
  try {
    return window.localStorage.getItem(RING_SETTING_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setRingEnabled(enabled: boolean): void {
  try {
    if (enabled) window.localStorage.removeItem(RING_SETTING_KEY);
    else window.localStorage.setItem(RING_SETTING_KEY, 'off');
  } catch {
    // Storage may be blocked (private window): the setting simply does not persist.
  }
}

/** A ring: two notes, then a pause (the pattern lasts 3 s). */
const NOTES = [
  { frequency: 523.25, start: 0, length: 0.35 },
  { frequency: 659.25, start: 0.4, length: 0.35 },
] as const;
const PATTERN_MS = 3_000;
const PEAK_GAIN = 0.06;

type AudioContextConstructor = typeof AudioContext;

const contextConstructor = (): AudioContextConstructor | undefined => {
  const scope = window as unknown as {
    AudioContext?: AudioContextConstructor;
    webkitAudioContext?: AudioContextConstructor;
  };
  return scope.AudioContext ?? scope.webkitAudioContext;
};

function playPattern(context: AudioContext): void {
  const now = context.currentTime;
  for (const note of NOTES) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = note.frequency;
    const start = now + note.start;
    const end = start + note.length;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(PEAK_GAIN, start + 0.04);
    gain.gain.linearRampToValueAtTime(0, end);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(start);
    oscillator.stop(end + 0.02);
  }
}

/**
 * Starts the ring if the setting is on and the browser allows sound; returns the function that
 * stops it (always safe to call, once or more).
 */
export function startRingtone(): () => void {
  if (!isRingEnabled()) return () => undefined;
  let context: AudioContext | undefined;
  let timer: ReturnType<typeof setInterval> | undefined;
  try {
    const Constructor = contextConstructor();
    if (!Constructor) return () => undefined;
    const audio = new Constructor();
    context = audio;
    const ring = () => {
      try {
        if (audio.state === 'suspended') {
          // Allowed once the person has interacted with the page; else it stays suspended: silent.
          void audio
            .resume()
            .then(() => {
              if (context === audio && audio.state === 'running') playPattern(audio);
            })
            .catch(() => undefined);
        } else if (audio.state === 'running') {
          playPattern(audio);
        }
      } catch {
        // A ring that cannot play is not worth an error.
      }
    };
    ring();
    timer = setInterval(ring, PATTERN_MS);
  } catch {
    // No audio on this device or the context was refused: silent.
  }
  return () => {
    if (timer !== undefined) clearInterval(timer);
    timer = undefined;
    const closing = context;
    context = undefined;
    if (closing) void closing.close().catch(() => undefined);
  };
}
